import React, {useEffect, useState} from 'react';
import {Alert, Button, Snackbar} from '@mui/material';
import Popup from '../../../../components/popup';
import {SamplesReview} from './SamplesReview';
import {useNavigate, useParams} from 'react-router-dom';
import {getAppConfig} from '../../../../utils/config';
import {useAuth0} from '@auth0/auth0-react';
import {trimKeysAndValuesInObject, sortObjectKeys} from '../../../../utils/helpers';
import {RnaReview} from './RnaReview';
import {ChipReview} from './ChipReview';
import {AtacReview} from './AtacReview';
import type {SampleErrors} from './types';

/**
 * Checks the samples for errors:
 * - Fewer files than expected per sample
 * - Duplicate sample names
 * - Files with status other than 'ready' or 'unknown'
 *
 * @return An object with arrays of sample IDs for each error type. The arrays are empty if there are no errors
 */
const checkSamples = (samplesArr: any[], expectedFileCount: any): SampleErrors => {
    const errorsArr: SampleErrors = {
        missingFiles: [],
        duplicateName: [],
        fileStatus: []
    };
    const nameSet = new Set();
    samplesArr?.forEach((sample: any) => {
        const normalizedName = sample?.name?.trim().toLowerCase();
        if (nameSet?.has(normalizedName)) {
            errorsArr?.duplicateName?.push(sample.id);
        } else {
            nameSet.add(normalizedName);
        }
        if (expectedFileCount - sample?.files.length > 0) {
            errorsArr.missingFiles.push(sample.id);
        }
        // TODO: Why filter unknown? That is the default before uploading.
        // Surely a file still stuck in unknown is an error?
        if (sample.files.some((f: any) => f.status !== 'ready' && f.status !== 'unknown')) {
            errorsArr.fileStatus.push(sample.id);
        }
    });
    return errorsArr;
};

/**
 * Loops through samples' other_categories, collects keys and their unique values.
 *
 * @return New shallow copy object with the keys and their unique values
 */
const parseCategories = (samples: {other_categories: Record<string, any>}[]): Record<string, any[]> => {
    const result: Record<string, Set<any>> = {};
    samples.forEach(sample => {
        const categories = sample.other_categories;
        for (const key in categories) {
            if (!result[key]) {
                result[key] = new Set();
            }
            // TODO: Can the value be an object, i.e., are we passing by reference here?
            result[key].add(categories[key]);
        }
    });
    const finalResult: Record<string, any[]> = {};
    for (const key in result) {
        finalResult[key] = Array.from(result[key]);
    }
    return finalResult;
};

interface Step4Props {
    experiment: any;
    setStep: (step: number) => void;
    setFormData: (data: any) => void;
    expectedFilenamesBySampleId?: Record<string, string[]>;
}

/**
 * Component that enables the user to review and edit the samples and their
 * variables. Also offers adding new samples, deleting existing ones, and
 * fixing failed uploads.
 */
const Step4 = ({experiment, setStep, setFormData, expectedFilenamesBySampleId}: Step4Props) => {
    const {id} = useParams();
    const {getAccessTokenSilently} = useAuth0();
    const {apiHost} = getAppConfig();
    const navigate = useNavigate();
    const [samplesToReview, setSamplesToReview] = useState<any>(null);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [errors, setErrors] = useState<SampleErrors>({missingFiles: [], duplicateName: [], fileStatus: []});
    const [samples, setSamples] = useState<any[]>([]);

    const globalValues = sortObjectKeys(parseCategories(samples));

    /**
     * Handle an edit (rename) to a variable made in the Variables and Values
     * table. Sample objects are updated in the database (repeated endpoint
     * calls). Finally the page's state is refefreshed with reFetchSamples.
     */
    const handleVariableChange = async (newKey: string, oldKey: string) => {
        const editPromises = samples?.map((sample: any) => {
            // Can this part (except for the editSample call) not be a pure function like renameOtherCategoriesKey?
            if (sample.other_categories && oldKey in sample.other_categories) {
                const oldValue = sample.other_categories[oldKey];
                delete sample.other_categories[oldKey];
                sample.other_categories[newKey.trim()] = oldValue;
                return editSample(sample);
            } else {
                return Promise.resolve();
            }
        });
        try {
            await Promise.all(editPromises);
            setOpenSnackbar(true);
        } catch (error) {
            console.error('Error updating samples:', error);
        } finally {
            reFetchSamples();
        }
    };

    /**
     * Handle an edit (rename) to a value made in the Variables and Values table.
     */
    const handleValueChange = async (newValue: string, key: string, index: number) => {
        const changedSamples: any[] = [];
        const oldValue = globalValues[key][index];
        samples.forEach((sample: any) => {
            if (sample.other_categories[key] === oldValue) {
                sample.other_categories[key] = newValue;
                changedSamples.push(sample);
            }
        });
        try {
            await Promise.all(
                changedSamples.map((s: any) => {
                    return editSample(s);
                })
            );
        } catch (err) {
            console.error(err);
        } finally {
            setSamplesToReview({changedSamples, oldValue, newValue, key});
            reFetchSamples();
        }
    };

    /**
     * Fetches all samples for the experiment from the database. Samples are
     * checked for errors and errors are set to state. Regardless of errors,
     * samples are set to state as well.
     */
    const reFetchSamples = async () => {
        const response = await fetch(`${apiHost}/experiment/${id}/sample`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await getAccessTokenSilently()}`
            }
        });
        const result = await response.json();
        const errors = checkSamples(result, experiment?.files_per_sample);
        setErrors(errors);
        setSamples(result);
    };

    /**
     * Updates the sample in the database. The sample is sanitised first, i.e.,
     * - keys and values of other_categories are trimmed
     * - archived are set to false
     * - null values are set to empty strings or 0, depending
     */
    const editSample = async (sample: any) => {
        try {
            // Sanitise
            sample.other_categories = trimKeysAndValuesInObject(sample.other_categories);
            Object.keys(sample).forEach(key => {
                if (key === 'archived') {
                    sample[key] = false;
                } else if (sample[key] === null) {
                    if (key === 'number_of_files') {
                        sample[key] = 0;
                    } else {
                        sample[key] = '';
                    }
                }
            });
            // Update sample
            const response = await fetch(`${apiHost}/experiment/${id}/sample/${sample.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${await getAccessTokenSilently()}`
                },
                body: JSON.stringify(sample)
            });
            if (!response.ok) {
                throw new Error(`Error updating sample: ${response.statusText}`);
            }
        } catch (err) {
            console.error(err);
        }
    };

    // Do an initial fetch seperately from the openSnackbar one below. At the
    // start, that message is not open yet, so will not actually fetch samples
    // due to the if check there.
    useEffect(() => {
        reFetchSamples();
    }, []);

    // Opening of the snackbar (tends to) mean(s) that a file was added or some
    // other change. Fetching again to ensure we are in sync with bff. Only
    // doing it on open, not on close too.
    useEffect(() => {
        if (openSnackbar) {
            reFetchSamples();
        }
    }, [openSnackbar]);

    /**
     * Sets form data and moves to next step
     */
    const handleSave = () => {
        setFormData((state: any) => ({...state, step_three_details: globalValues}));
        setStep(4);
    };

    let typeSpecificReview = null;
    if (experiment?.sequencing_type === 'RNA Seq') {
        typeSpecificReview = (
            <RnaReview
                globalValues={globalValues}
                samples={samples}
                handleValueChange={handleValueChange}
                handleVariableChange={handleVariableChange}
                experiment={experiment}
                reFetchSamples={reFetchSamples}
                errors={errors}
                editSample={editSample}
                setOpenSnackbar={setOpenSnackbar}
            />
        );
    } else if (experiment?.sequencing_type === 'ChIP Seq') {
        typeSpecificReview = (
            <ChipReview
                samples={samples}
                experiment={experiment}
                errors={errors}
                reFetchSamples={reFetchSamples}
                expectedFilenamesBySampleId={expectedFilenamesBySampleId}
            />
        );
    } else if (experiment?.sequencing_type === 'ATAC Seq') {
        typeSpecificReview = (
            <AtacReview
                samples={samples}
                experiment={experiment}
                errors={errors}
                reFetchSamples={reFetchSamples}
                expectedFilenamesBySampleId={expectedFilenamesBySampleId}
            />
        );
    }

    return (
        <>
            {typeSpecificReview}
            <Button
                variant="contained"
                color="primary"
                sx={{my: 2}}
                fullWidth
                onClick={handleSave}
                disabled={samples.length === 0 || Object.values(errors).some((arr: any) => arr.length > 0)}>
                Save and Continue
            </Button>
            <Button variant="outlined" color="primary" onClick={() => navigate(`/`)} fullWidth>
                Save Progress and Exit
            </Button>
            <Popup isOpen={Boolean(samplesToReview)} onClose={() => setSamplesToReview(null)}>
                <SamplesReview samplesToReview={samplesToReview} onClose={() => setSamplesToReview(null)} />
            </Popup>
            <Snackbar
                open={openSnackbar}
                autoHideDuration={5000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{vertical: 'top', horizontal: 'center'}}>
                <Alert severity="success">Changes were saved successfully!</Alert>
            </Snackbar>
        </>
    );
};

export default Step4;
